import React, { useState, useEffect, ReactNode, useCallback } from 'react';
import {
  Card,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  TextField,
  Typography,
  makeStyles,
  Link,
  Box,
  Grid
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import {
  AddCircleOutline,
  Delete,
  ChevronRight,
  ChevronLeft,
  GetApp,
  Publish
} from '@material-ui/icons';
import InputMask from 'react-input-mask';
import { api } from '@services/api';
import { ASO } from '@data/tipoAso';
import { status } from '@data/status';
import { getFileName, verifyUpLoads } from '../../../../utils/masks';
import { Button as CustomButton } from '@components/Button';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getDataScheduling } from '@api/get-data-schedule';
import { postDownloadFicha } from '@api/post-download-ficha';
import { FichaClinica } from '@pages/Agendamentos/PDF/fichaClinica';
import { Guia } from '../../PDF/gerarGuia';
import { PDF } from '../../PDF/gerarPdf';

import { Button as MuiButton, ButtonProps, styled } from '@mui/material';

import { GuiaHibrido } from '@pages/Agendamentos/PDF/guiahibrido';
import pdfMake from 'pdfmake/build/pdfmake';
import { AppError } from '@utils/AppError';
import { PDFHibrido } from '@pages/Agendamentos/PDF/gerarpdfhibrido';
import { LinkKitButton } from '@components/LinkKitButton';
type VariantStyle =
  | 'DARK_PINK'
  | 'LIGHT_PINK'
  | 'PURPLE'
  | 'PRIMARY'
  | 'SECONDARY';

interface CustomButtonProps extends ButtonProps {
  icon?: ReactNode;
  variantstyle?: VariantStyle;
}

interface ISelectedExames {
  id_exame_ghe: number;
  cod_ghe: string;
  nome: string;
}

interface INomeComercial {
  id_clinicas_credenciadas: number;
  nome_comercial: string;
  cpf_cnpj: string;
  cidade: string;
  estado: string;
  tipo: string;
}

interface IStatus {
  location: {
    state: {
      id_agendamento: number;
      tipo_agendamento: string;
      num_agenda?: string;
    };
  };
}
const StyledButton = styled(MuiButton)<{ variantstyle?: VariantStyle }>(
  ({ theme, variantstyle }) => ({
    ...(variantstyle === 'DARK_PINK' && {
      backgroundColor: '#e91e63',
      color: 'white',
      '&:hover': {
        backgroundColor: '#ad1457'
      }
    }),
    ...(variantstyle === 'LIGHT_PINK' && {
      backgroundColor: '#f8bbd0',
      color: '#c2185b',
      '&:hover': {
        backgroundColor: '#f48fb1'
      }
    }),
    ...(variantstyle === 'PURPLE' && {
      backgroundColor: '#9c27b0',
      color: 'white',
      '&:hover': {
        backgroundColor: '#7b1fa2'
      }
    })
  })
);

export const Button = ({
  icon,
  variantstyle,
  children,
  ...props
}: CustomButtonProps) => {
  return (
    <StyledButton variantstyle={variantstyle} startIcon={icon} {...props}>
      {children}
    </StyledButton>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: '#f9f9f9'
  },
  paper: {
    padding: theme.spacing(4),
    borderRadius: 12,
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
  },
  title: {
    marginBottom: theme.spacing(4),
    fontWeight: 'bold',
    textAlign: 'center',
    color: '#2c3e50',
    position: 'relative',
    paddingBottom: theme.spacing(1),
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: 80,
      height: 4,
      backgroundColor: '#0D9C5B',
      borderRadius: 2
    }
  },
  sectionTitle: {
    margin: `${theme.spacing(3)}px 0 ${theme.spacing(2)}px`,
    fontWeight: 600
  },
  formRow: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap'
  },
  transferContainer: {
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'center',
    margin: `${theme.spacing(3)}px 0`
  },
  examCard: {
    flex: 1,
    border: '1px solid #e0e0e0',
    borderRadius: 12,
    maxHeight: 400,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  examList: {
    overflowY: 'auto',
    flex: 1
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(4),
    gap: theme.spacing(2)
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4)
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1)
  },
  agendadoBadge: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    padding: '2px 8px',
    borderRadius: 12,
    fontSize: 12,
    marginLeft: 8
  },
  downloadButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(3),
    gap: theme.spacing(2),
    flexWrap: 'wrap'
  },
  uploadButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    margin: theme.spacing(2, 0),
    alignItems: 'center'
  },
  fileName: {
    marginLeft: theme.spacing(1),
    fontStyle: 'italic'
  }
}));

export const AtendimentoHibrido: React.FC<IStatus> = (props) => {
  const classes = useStyles();
  const idAgendamento = String(props.location.state.id_agendamento);

  const {
    data: getDataSchedule,
    error: errorDataSchedule,
    isFetching
  } = useQuery({
    queryKey: ['getSchedule', idAgendamento],
    queryFn: () => getDataScheduling(idAgendamento)
  });
  const { mutateAsync: fetchFichaClinica } = useMutation({
    mutationFn: postDownloadFicha,
    onSuccess(data) {
      FichaClinica(data.data);
    },
    onError(error) {
      console.error('Erro ao baixar ficha clínica:', error);
    }
  });

  async function downloadFile(arquivo: string | undefined) {
    try {
      const data = {
        arquivo: String(arquivo) // Garante que o valor será uma string
      };

      const response = await api.post(`/ravim/download`, data);
      const url = response.data.dados;
      const link = document.createElement('a');
      link.href = url;
      link.download = String(arquivo).replace(/[_]/g, ' '); // Converte para string
      link.dispatchEvent(new MouseEvent('click'));
    } catch (err) {
      console.error('Erro ao baixar arquivo:', err);
    }
  }

  // Função para gerar PDF
  const reqpdf = useCallback(
    async (
      tipo: number,
      cpf_colaborador: string,
      cpf_cnpj_comer: string,
      num_agenda: string,
      cnpj_empresa:string
    ) => {
      try {
        const res = await api.post(`/ravim/pdf`, {
          cpf_colaborador,
          cnpj_cpf_comer: cpf_cnpj_comer,
          num_agenda,
          cnpj_empresa
        });

        if (tipo === 1) {
          // console.log(res.data)
          GuiaHibrido(res.data,'');
        } else {
          // console.log('estou aqui', res.data)
          
          PDFHibrido(res.data, '');
          //FichaClinica(res.data);
        }
      } catch (error) {
        if (error instanceof AppError) {
          console.log(error);
        }
      }
    },
    []
  );

  const [loading, setLoading] = useState({
    inicial: true,
    credenciados: false,
    examesDisponiveis: false,
    enviando: false,
    upload: false
  });

  const [error, setError] = useState('');
  const [agendamentoData, setAgendamentoData] = useState<any>(null);
  const [statusAgendamento, setStatusAgendamento] = useState('Solicitado');
  const [cpfColab, setCpfColab] = useState('');
  const [nomeColab, setNomeColab] = useState('');
  const [tipoAso, setTipoAso] = useState('');
  const [obs, setObs] = useState('');
  const [atendimento, setAtendimento] = useState('');
  const [clinicaSelecionada, setClinicaSelecionada] =
    useState<INomeComercial | null>(null);
  const [dataAgendamento, setDataAgendamento] = useState('');
  const [horarioAgendamento, setHorarioAgendamento] = useState('');
  const [ordemChegada, setOrdemChegada] = useState('Sim');
  const [linkDoAtendimento, setLinkDoAtendimento] = useState('');
  const [snack, setSnack] = useState(false);
  const [message, setMessage] = useState('');

  const PRESENCIAL_PAGE = 'Hibrido';

  // Estados para exames
  const [checked, setChecked] = useState<ISelectedExames[]>([]);
  const [examesDisponiveis, setExamesDisponiveis] = useState<ISelectedExames[]>(
    []
  );
  const [examesSelecionados, setExamesSelecionados] = useState<
    ISelectedExames[]
  >([]);
  const [examesAgendados, setExamesAgendados] = useState<any[]>([]);
  const [credenciados, setCredenciados] = useState<INomeComercial[]>([]);

  // Estados para upload/download
  const [arquivoASO, setArquivoASO] = useState<File | null>(null);
  const [arquivoProntuario, setArquivoProntuario] = useState<File | null>(null);
  const [arquivosDisponiveis, setArquivosDisponiveis] = useState({
    aso: '',
    prontuario: ''
  });

  // Carrega dados iniciais do agendamento
  useEffect(() => {
    const carregarDadosIniciais = async () => {
      try {
        const response = await api.get(
          `/ravim/update-scheduling/${props.location.state.id_agendamento}`
        );
        const dadosAgendamento = response.data[0];

        setAgendamentoData(dadosAgendamento);
        setCpfColab(dadosAgendamento.cpf_colaborador || '');
        setNomeColab(dadosAgendamento.nome || '');
        setTipoAso(dadosAgendamento.tipo_aso || '');
        setObs(dadosAgendamento.observacao || '');
        setStatusAgendamento(dadosAgendamento.status || 'Solicitado');
        setDataAgendamento(dadosAgendamento.data_agendamento || '');
        setHorarioAgendamento(dadosAgendamento.horario || '');
        setLinkDoAtendimento(dadosAgendamento.linkDoatendimento || '');
        setOrdemChegada(dadosAgendamento.ordem_chegada || 'Sim');

        // Verifica e carrega arquivos existentes
        if (dadosAgendamento.arquivo_aso) {
          setArquivosDisponiveis((prev) => ({
            ...prev,
            aso: dadosAgendamento.arquivo_aso
          }));
        }

        if (dadosAgendamento.arquivo_prontuario) {
          setArquivosDisponiveis((prev) => ({
            ...prev,
            prontuario: dadosAgendamento.arquivo_prontuario
          }));
        }

        await carregarExamesAgendados();
      } catch (error) {
        console.error('Erro ao carregar dados iniciais:', error);
        setError('Erro ao carregar dados do agendamento');
      } finally {
        setLoading((prev) => ({ ...prev, inicial: false }));
      }
    };

    if (props.location.state.id_agendamento) {
      carregarDadosIniciais();
    }
  }, [props.location.state.id_agendamento]);

  // Carrega exames já agendados
  const carregarExamesAgendados = async () => {
    try {
      const response = await api.get(
        `/ravim/exames-hibrido-colab/${props.location.state.id_agendamento}`
      );

      const examesTransformados = response.data.map((exame: any) => ({
        id_exame_hibrido: exame.id_exame_hibrido,
        cpf_cnpj_comer: exame.cpf_cnpj_comer,
        nome_comercial: exame.nome_comercial,
        data_agendamento: exame.data_agendamento,
        horario: exame.horario || '',
        ordem_chegada:
          exame.ordem_chegada === 'None' ? null : exame.ordem_chegada,
        exames: [
          {
            id_exame_ghe: exame.id_exame_hibrido,
            cod_ghe: 'EX-' + exame.id_exame_hibrido,
            nome: exame.exame_empresa
          }
        ],
        linkDoatendimento: exame.linkDoatendimento || undefined,
        tipo: exame.tipo,
        tipo_agendamento: exame.tipo_agendamento,
        num_agenda: exame.num_agenda
      }));

      setExamesAgendados(examesTransformados);

      if (examesTransformados.length > 0) {
        const primeiroExame = examesTransformados[0];
        setDataAgendamento(primeiroExame.data_agendamento);
        setHorarioAgendamento(primeiroExame.horario);
        setLinkDoAtendimento(primeiroExame.linkDoatendimento || '');
        setOrdemChegada(primeiroExame.ordem_chegada || 'Sim');

        buscarClinicaPorCnpj(primeiroExame.cpf_cnpj_comer);
      }
    } catch (error) {
      console.error('Erro ao carregar exames agendados:', error);
      setError('Erro ao carregar exames já agendados');
    }
  };

  // Busca clínica por CNPJ/CPF
  const buscarClinicaPorCnpj = async (cpf_cnpj: string) => {
    try {
      const response = await api.post('/ravim/telemed-clinicas', {
        tipo_agendamento: props.location.state.tipo_agendamento,
        tipo: atendimento
      });

      const clinicaEncontrada = response.data.find(
        (clinica: INomeComercial) => clinica.cpf_cnpj === cpf_cnpj
      );

      if (clinicaEncontrada) {
        setClinicaSelecionada(clinicaEncontrada);
      }
    } catch (error) {
      console.error('Erro ao buscar clínica:', error);
    }
  };

  // Busca credenciados
  const buscarCredenciados = async () => {
    if (!atendimento) return;

    setLoading((prev) => ({ ...prev, credenciados: true }));
    try {
      const response = await api.post('/ravim/telemed-clinicas', {
        tipo_agendamento: props.location.state.tipo_agendamento,
        tipo: atendimento
      });

      setCredenciados(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar credenciados:', error);
    } finally {
      setLoading((prev) => ({ ...prev, credenciados: false }));
    }
  };

  // Busca exames disponíveis
  const buscarExamesDisponiveis = async () => {
    if (!cpfColab || !tipoAso) return;

    setLoading((prev) => ({ ...prev, examesDisponiveis: true }));
    try {
      const response = await api.post('/ravim/exames-hibrido', {
        cpf_colaborador: cpfColab,
        tipo_aso: tipoAso
      });

      const examesAgendadosIds = examesAgendados.flatMap((e) =>
        e.exames.map((ex: { id_exame_ghe: any }) => ex.id_exame_ghe)
      );
      setExamesDisponiveis(
        (response.data || []).filter(
          (ex: ISelectedExames) => !examesAgendadosIds.includes(ex.id_exame_ghe)
        )
      );
    } catch (error) {
      console.error('Erro ao carregar exames disponíveis:', error);
    } finally {
      setLoading((prev) => ({ ...prev, examesDisponiveis: false }));
    }
  };

  // Manipulação da seleção de exames
  const handleToggle = (item: ISelectedExames) => () => {
    setChecked((prev) =>
      prev.includes(item)
        ? prev.filter((i) => i.id_exame_ghe !== item.id_exame_ghe)
        : [...prev, item]
    );
  };

  const moverParaSelecionados = () => {
    const selecionados = examesDisponiveis.filter((ex) => checked.includes(ex));
    setExamesSelecionados([...examesSelecionados, ...selecionados]);
    setExamesDisponiveis(
      examesDisponiveis.filter((ex) => !checked.includes(ex))
    );
    setChecked(checked.filter((item) => !selecionados.includes(item)));
  };

  const moverParaDisponiveis = () => {
    const selecionados = examesSelecionados.filter((ex) =>
      checked.includes(ex)
    );
    setExamesDisponiveis([...examesDisponiveis, ...selecionados]);
    setExamesSelecionados(
      examesSelecionados.filter((ex) => !checked.includes(ex))
    );
    setChecked(checked.filter((item) => !selecionados.includes(item)));
  };

  // Adiciona novos exames ao agendamento
  const adicionarExames = () => {
    if (
      !clinicaSelecionada ||
      examesSelecionados.length === 0 ||
      !dataAgendamento
    ) {
      setSnack(true);
      setMessage('Preencha todos os campos obrigatórios');
      return;
    }

    const novoAgendamento = {
      id_exame_hibrido: Date.now(), // ID temporário
      cpf_cnpj_comer: clinicaSelecionada.cpf_cnpj,
      nome_comercial: clinicaSelecionada.nome_comercial,
      data_agendamento: dataAgendamento,
      horario: horarioAgendamento,
      ordem_chegada: atendimento === 'presencial' ? ordemChegada : null,
      exames: [...examesSelecionados],
      linkDoatendimento:
        atendimento === 'telemedicina' ? linkDoAtendimento : undefined,
      tipo: atendimento,
      tipo_agendamento: props.location.state.tipo_agendamento,
      num_agenda: props.location.state.num_agenda || ''
    };

    setExamesAgendados([...examesAgendados, novoAgendamento]);
    setExamesSelecionados([]);
    setChecked([]);
    setDataAgendamento('');
    setHorarioAgendamento('');
    setLinkDoAtendimento('');

    setSnack(true);
    setMessage('Exames adicionados ao agendamento');
  };

  // Remove exame do agendamento
  const removerExameAgendado = (id: number) => {
    const removido = examesAgendados.find((e) => e.id_exame_hibrido === id);
    if (removido) {
      setExamesAgendados(
        examesAgendados.filter((e) => e.id_exame_hibrido !== id)
      );
      setExamesDisponiveis([...examesDisponiveis, ...removido.exames]);
    }
  };

  // Manipuladores de upload de arquivos
  const handleFileASOChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setArquivoASO(event.target.files[0]);
    }
  };

  const handleFileProntuarioChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files && event.target.files[0]) {
      setArquivoProntuario(event.target.files[0]);
    }
  };

  // Envia arquivos para o servidor
  const enviarArquivos = async () => {
    if (!arquivoASO && !arquivoProntuario) {
      setSnack(true);
      setMessage('Selecione pelo menos um arquivo para enviar');
      return;
    }

    setLoading((prev) => ({ ...prev, upload: true }));

    const formData = new FormData();
    if (arquivoASO) formData.append('aso', arquivoASO);
    if (arquivoProntuario) formData.append('prontuario', arquivoProntuario);

    try {
      await api.post(
        `/ravim/upload/${props.location.state.id_agendamento}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setSnack(true);
      setMessage('Arquivos enviados com sucesso');

      // Atualiza a lista de arquivos disponíveis
      if (arquivoASO) {
        setArquivosDisponiveis((prev) => ({
          ...prev,
          aso: arquivoASO.name
        }));
      }

      if (arquivoProntuario) {
        setArquivosDisponiveis((prev) => ({
          ...prev,
          prontuario: arquivoProntuario.name
        }));
      }

      // Limpa os arquivos após envio
      setArquivoASO(null);
      setArquivoProntuario(null);
    } catch (error) {
      console.error('Erro ao enviar arquivos:', error);
      setSnack(true);
      setMessage('Erro ao enviar arquivos');
    } finally {
      setLoading((prev) => ({ ...prev, upload: false }));
    }
  };

  // Envia o agendamento atualizado para a API
  const enviarAgendamento = async () => {
    if (examesAgendados.length === 0) {
      setSnack(true);
      setMessage('Adicione pelo menos um exame ao agendamento');
      return;
    }

    setLoading((prev) => ({ ...prev, enviando: true }));
    try {
      const response = await api.put(
        `/ravim/atua-agendamento-hibrido`,
        {
          num_agenda: idAgendamento,
          status: statusAgendamento,
          tipo_aso: tipoAso,
          observacao: obs,
          exames: examesAgendados,
          data_agendamento: dataAgendamento,
          horario: horarioAgendamento,
          linkDoatendimento: linkDoAtendimento,
          ordem_chegada: ordemChegada,
          cpf_colaborador: cpfColab,
          arquivo_prontuario:arquivoProntuario,
          arquivo_aso:arquivoASO
        }
      );

      setSnack(true);
      setMessage(response.data.message || 'Agendamento atualizado com sucesso');
      setTimeout(() => {
        window.location.replace('/agendamentos');
      }, 2000);
    } catch (error) {
      console.error('Erro ao atualizar agendamento:', error);
      setSnack(true);
      setMessage('Erro ao atualizar agendamento');
    } finally {
      setLoading((prev) => ({ ...prev, enviando: false }));
    }
  };

  // Efeitos para carregar dados quando dependências mudam
  useEffect(() => {
    buscarCredenciados();
  }, [atendimento]);

  useEffect(() => {
    if (clinicaSelecionada && cpfColab && tipoAso) {
      buscarExamesDisponiveis();
    }
  }, [clinicaSelecionada, cpfColab, tipoAso, examesAgendados]);

  // Renderização condicional durante carregamento
  if (loading.inicial) {
    return (
      <Container maxWidth="lg" className={classes.root}>
        <Paper className={classes.loadingContainer}>
          <CircularProgress />
          <Typography variant="body1" style={{ marginLeft: 16 }}>
            Carregando dados do agendamento...
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" className={classes.root}>
        <Paper className={classes.paper}>
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" className={classes.root}>
      <Paper className={classes.paper}>
        {/* Seção de botões de download */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: 20,
            gap: 20
          }}
        >
          {verifyUpLoads(getDataSchedule?.arquivo_aso!) !== '' ? (
            <Button
              title="Download ASO"
              icon={<GetApp />}
              onClick={async () => {
                await downloadFile(
                  getFileName(String(getDataSchedule?.arquivo_aso))
                );
              }}
            />
          ) : (
            <></>
          )}
          {verifyUpLoads(getDataSchedule?.arquivo_prontuario!) !== '' ? (
            <Button
              title="Download Prontuário"
              icon={<GetApp />}
              onClick={async () => {
                await downloadFile(
                  getFileName(String(getDataSchedule?.arquivo_prontuario))
                );
              }}
            />
          ) : (
            <></>
          )}

          <Button
            icon={<GetApp style={{ fontSize: 20 }} />}
            onClick={() => {
              fetchFichaClinica({
                cpf_colaborador: getDataSchedule?.cpf_colaborador || '',
                cnpj_cpf_comer: getDataSchedule?.cpf_cnpj_comer || ''
              });
            }}
            title="Download KIT - Ficha clínica"
            variantstyle="PURPLE"
          >
            Download KIT - Ficha clínica
          </Button>

          <Button
            title="Download KIT - ASO"
            icon={<GetApp style={{ fontSize: 20 }} />}
            variantstyle="PURPLE"
            onClick={() =>
              reqpdf(
                2,
                getDataSchedule?.cpf_colaborador || '',
                getDataSchedule?.cpf_cnpj_comer || '',
                getDataSchedule?.num_agenda || '',
                getDataSchedule?.cnpj_empresa || '',

              )
            }
          >
            Download KIT - ASO
          </Button>

          <Button
            title="Download Guia Híbrido"
            icon={<GetApp style={{ fontSize: 20 }} />}
            variantstyle="DARK_PINK"
            onClick={() => {
              reqpdf(
                1,
                getDataSchedule?.cpf_colaborador || '',
                getDataSchedule?.cpf_cnpj_comer || '',
                getDataSchedule?.num_agenda || '',
                getDataSchedule?.cnpj_empresa || ''
              );
            }}
          >
            Download Guia Híbrido
          </Button>
        </div>
        <Typography variant="h4" className={classes.title}>
          Atendimento Híbrido
        </Typography>

        {/* Seção de Upload de Arquivos */}
        <Typography variant="h6" className={classes.sectionTitle}>
          Upload de Arquivos
        </Typography>
        <div className={classes.uploadButtons}>
          <input
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
            id="upload-aso"
            type="file"
            onChange={handleFileASOChange}
          />
          <label htmlFor="upload-aso">
            <Button variant="outlined" color="primary" startIcon={<Publish />}>
              Upload ASO
            </Button>
          </label>
          {arquivoASO && (
            <Typography className={classes.fileName}>
              {arquivoASO.name}
            </Typography>
          )}

          <input
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
            id="upload-prontuario"
            type="file"
            onChange={handleFileProntuarioChange}
          />
          <label htmlFor="upload-prontuario">
            <Button variant="outlined" color="primary" startIcon={<Publish />}>
              Upload Prontuário
            </Button>
          </label>
          {arquivoProntuario && (
            <Typography className={classes.fileName}>
              {arquivoProntuario.name}
            </Typography>
          )}

          {(arquivoASO || arquivoProntuario) && (
            <Button
              variant="contained"
              color="secondary"
              onClick={enviarArquivos}
              disabled={loading.upload}
              style={{ marginLeft: 'auto' }}
            >
              {loading.upload ? (
                <>
                  <CircularProgress size={24} style={{ marginRight: 8 }} />
                  Enviando...
                </>
              ) : (
                'Enviar Arquivos'
              )}
            </Button>
          )}
        </div>

        {/* Seção de Status */}
        <Typography
          variant="h6"
          className={classes.sectionTitle}
          style={{ marginTop: 16 }}
        >
          Status do Agendamento
        </Typography>
        <div className={classes.formRow}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusAgendamento}
              onChange={(e) => setStatusAgendamento(e.target.value as string)}
              label="Status"
            >
              {status.map((status, index) => (
                <MenuItem key={index} value={status.value}>
                  {status.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {/* Seção do Colaborador */}
        <Typography variant="h6" className={classes.sectionTitle}>
          Dados do Colaborador
        </Typography>
        <div className={classes.formRow}>
          <TextField
            label="CPF"
            variant="outlined"
            fullWidth
            value={cpfColab}
            InputProps={{
              readOnly: true
            }}
            disabled
          />
          <TextField
            label="Nome"
            variant="outlined"
            fullWidth
            value={nomeColab}
            InputProps={{
              readOnly: true
            }}
            disabled
          />
        </div>

        {/* Detalhes do Agendamento */}
        <>
          <Typography variant="h6" className={classes.sectionTitle}>
            Detalhes do Agendamento
          </Typography>
          <Grid container spacing={2} className={classes.formRow}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Data"
                type="date"
                value={dataAgendamento}
                onChange={(e) => setDataAgendamento(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Horário"
                type="time"
                value={horarioAgendamento}
                onChange={(e) => setHorarioAgendamento(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled={
                  ordemChegada === 'Sim' && atendimento === 'presencial'
                }
              />
            </Grid>
            {atendimento === 'telemedicina' && (
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Link do Atendimento"
                  value={linkDoAtendimento}
                  onChange={(e) => setLinkDoAtendimento(e.target.value)}
                  fullWidth
                />
              </Grid>
            )}
          </Grid>
          {atendimento === 'presencial' && (
            <div className={classes.formRow}>
              <FormControl component="fieldset" fullWidth>
                <Typography>Ordem de chegada</Typography>
                <RadioGroup
                  row
                  value={ordemChegada}
                  onChange={(e) => setOrdemChegada(e.target.value)}
                >
                  <FormControlLabel
                    value="Sim"
                    control={<Radio />}
                    label="Sim"
                  />
                  <FormControlLabel
                    value="Não"
                    control={<Radio />}
                    label="Não"
                  />
                </RadioGroup>
              </FormControl>
            </div>
          )}
        </>

        {/* Seção de Atendimento */}
        <Typography variant="h6" className={classes.sectionTitle}>
          Tipo de Atendimento
        </Typography>
        <RadioGroup
          row
          value={atendimento}
          onChange={(e) => {
            setAtendimento(e.target.value);
            setClinicaSelecionada(null);
          }}
        >
          <FormControlLabel
            value="telemedicina"
            control={<Radio />}
            label="Telemedicina"
          />
          <FormControlLabel
            value="presencial"
            control={<Radio />}
            label="Presencial"
          />
        </RadioGroup>

        {/* Seção de Clínica */}
        {atendimento && (
          <>
            <Typography variant="h6" className={classes.sectionTitle}>
              Selecione a Clínica
            </Typography>
            <Autocomplete
              options={credenciados}
              getOptionLabel={(option) =>
                `${option.nome_comercial} - ${option.cidade}/${option.estado}`
              }
              onChange={(_, value) => setClinicaSelecionada(value)}
              value={clinicaSelecionada}
              loading={loading.credenciados}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Clínica"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading.credenciados ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          </>
        )}

        {/* Tipo de ASO */}
        <div className={classes.formRow} style={{ marginTop: 20 }}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel>Tipo de ASO</InputLabel>
            <Select
              value={tipoAso}
              onChange={(e) => setTipoAso(e.target.value as string)}
              label="Tipo de ASO"
            >
              {ASO.map((aso, index) => (
                <MenuItem key={index} value={aso.value}>
                  {aso.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {/* Observações */}
        <div className={classes.formRow}>
          <TextField
            label="Observações"
            variant="outlined"
            multiline
            rows={4}
            fullWidth
            value={obs}
            onChange={(e) => setObs(e.target.value)}
          />
        </div>

        {/* Seleção de novos exames */}
        {clinicaSelecionada && (
          <>
            <Typography variant="h6" className={classes.sectionTitle}>
              Adicionar Novos Exames
            </Typography>

            {loading.examesDisponiveis ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress />
                <Box ml={2}>Carregando exames disponíveis...</Box>
              </Box>
            ) : (
              <>
                <div className={classes.transferContainer}>
                  {/* Exames disponíveis */}
                  <Card className={classes.examCard}>
                    <Box p={2} borderBottom="1px solid #e0e0e0">
                      <Typography
                        variant="subtitle1"
                        style={{ fontWeight: 600 }}
                      >
                        Disponíveis ({examesDisponiveis.length})
                      </Typography>
                    </Box>
                    <List className={classes.examList}>
                      {examesDisponiveis.length > 0 ? (
                        examesDisponiveis.map((item) => (
                          <ListItem
                            key={item.id_exame_ghe}
                            button
                            onClick={handleToggle(item)}
                            selected={checked.includes(item)}
                          >
                            <ListItemIcon>
                              <Checkbox
                                edge="start"
                                checked={checked.includes(item)}
                                tabIndex={-1}
                              />
                            </ListItemIcon>
                            <ListItemText primary={item.nome} />
                          </ListItem>
                        ))
                      ) : (
                        <ListItem>
                          <ListItemText primary="Nenhum exame disponível" />
                        </ListItem>
                      )}
                    </List>
                  </Card>

                  {/* Botões de transferência */}
                  <Box display="flex" flexDirection="column" gridGap={4}>
                    <Button
                      variant="contained"
                      onClick={moverParaSelecionados}
                      disabled={
                        checked.filter((item) =>
                          examesDisponiveis.includes(item)
                        ).length === 0
                      }
                      style={{
                        minWidth: 40,
                        width: 40,
                        height: 40,
                        borderRadius: '50%'
                      }}
                    >
                      <ChevronRight />
                    </Button>
                    <Button
                      variant="contained"
                      onClick={moverParaDisponiveis}
                      disabled={
                        checked.filter((item) =>
                          examesSelecionados.includes(item)
                        ).length === 0
                      }
                      style={{
                        minWidth: 40,
                        width: 40,
                        height: 40,
                        borderRadius: '50%'
                      }}
                    >
                      <ChevronLeft />
                    </Button>
                  </Box>

                  {/* Exames selecionados */}
                  <Card className={classes.examCard}>
                    <Box p={2} borderBottom="1px solid #e0e0e0">
                      <Typography
                        variant="subtitle1"
                        style={{ fontWeight: 600 }}
                      >
                        Selecionados ({examesSelecionados.length})
                      </Typography>
                    </Box>
                    <List className={classes.examList}>
                      {examesSelecionados.length > 0 ? (
                        examesSelecionados.map((item) => (
                          <ListItem
                            key={item.id_exame_ghe}
                            button
                            onClick={handleToggle(item)}
                            selected={checked.includes(item)}
                          >
                            <ListItemIcon>
                              <Checkbox
                                edge="start"
                                checked={checked.includes(item)}
                                tabIndex={-1}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={item.nome}
                              secondary={`Código: ${item.cod_ghe}`}
                            />
                          </ListItem>
                        ))
                      ) : (
                        <ListItem>
                          <ListItemText primary="Nenhum exame selecionado" />
                        </ListItem>
                      )}
                    </List>
                  </Card>
                </div>

                {/* Botão para adicionar */}
                {examesSelecionados.length > 0 && (
                  <div style={{ textAlign: 'right', marginTop: 16 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AddCircleOutline />}
                      onClick={adicionarExames}
                      disabled={!dataAgendamento}
                    >
                      Adicionar Exames
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Exames já agendados */}
        {examesAgendados.length > 0 && (
          <Box mt={4}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Exames Agendados
            </Typography>
            {examesAgendados.map((agendamento) => (
              <Card
                key={agendamento.id_exame_hibrido}
                style={{ marginBottom: 16 }}
              >
                <Box p={2} position="relative">
                  <IconButton
                    style={{ position: 'absolute', top: 8, right: 8 }}
                    onClick={() =>
                      removerExameAgendado(agendamento.id_exame_hibrido)
                    }
                  >
                    <Delete color="error" />
                  </IconButton>

                  <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    {agendamento.nome_comercial} ({agendamento.tipo})
                  </Typography>

                  <Typography variant="body2">
                    {agendamento.data_agendamento}{' '}
                    {agendamento.horario && `às ${agendamento.horario}`}
                  </Typography>

                  {agendamento.ordem_chegada && (
                    <Typography variant="body2">
                      Ordem de chegada: {agendamento.ordem_chegada}
                    </Typography>
                  )}

                  {agendamento.linkDoatendimento && (
                    <Typography variant="body2">
                      Link:{' '}
                      <Link
                        href={agendamento.linkDoatendimento}
                        target="_blank"
                      >
                        {agendamento.linkDoatendimento}
                      </Link>
                    </Typography>
                  )}

                  <Typography
                    variant="body2"
                    style={{ marginTop: 8, fontWeight: 500 }}
                  >
                    Exames:
                  </Typography>

                  <List dense>
                    {agendamento.exames.map((exame: any) => (
                      <ListItem key={exame.id_exame_ghe}>
                        <ListItemText primary={exame.nome} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Card>
            ))}
          </Box>
        )}

        {/* Botões de ação */}
        <div className={classes.actionButtons}>
          {getDataSchedule?.token_kit && (
            <LinkKitButton token_kit={getDataSchedule.token_kit} />
          )}
          <Button variant="outlined" color="primary" href="/agendamentos">
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={examesAgendados.length === 0 || loading.enviando}
            onClick={enviarAgendamento}
          >
            {loading.enviando ? (
              <>
                <CircularProgress size={24} style={{ marginRight: 8 }} />
                Salvando...
              </>
            ) : (
              'Finalizar Agendamento'
            )}
          </Button>
        </div>
      </Paper>

      <Snackbar
        open={snack}
        autoHideDuration={4000}
        onClose={() => setSnack(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box bgcolor="#4caf50" color="white" p={2} borderRadius={4}>
          {message}
        </Box>
      </Snackbar>
    </Container>
  );
};
function createGuiaHibridoPdf(dadosParaGuia: any): import("pdfmake/interfaces").TDocumentDefinitions {
  throw new Error('Function not implemented.');
}

function setSeverity(arg0: string) {
  throw new Error('Function not implemented.');
}

